import React from 'react';
import styled from 'styled-components';
import { breakpoint, theme } from '../../theme';
import { UIText } from '../typography';
import { ConditionalWrapper } from '../../utils';

export type ContentTileProps = {
  link?: string;
  openInNewTab?: boolean;
  image?: string;
  text?: string | number;
  altText?: string;
  totalTiles?: number;
};

const TileWrapper = styled.div`
  display: flex;
  flex-basis: 50%;
  padding: ${theme.spacings[5].px} 0;
  flex-direction: column;
  border: 1px solid ${theme.colors.neutrals.solidGrays[6]};
  border-top: 0;

  ${breakpoint('md')`
    flex-basis:33.33%;
    `}

  ${breakpoint('lg')<{ numberOfTiles: number }>`
    flex-basis:${({ numberOfTiles }) =>
      numberOfTiles <= 5
        ? '100%'
        : numberOfTiles === 6
        ? '33.33%'
        : numberOfTiles === 8
        ? '25%'
        : '20%'};
   `}
`;

const ContentTileContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacings[2].px};
  min-height: 60px;
`;

const StyledImage = styled.img`
  aspect-ratio: 4/3;
  width: 100%;
  max-width: 128px;
  object-fit: contain;
  ${breakpoint('sm')`
    max-width: 160px;
    `}
`;

const StyledLink = styled.a`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacings[2].px};
  min-height: 60px;
`;

const TextContainer = styled(UIText).attrs({
  size: 'lg',
})`
  font-weight: bold;
`;

export const ContentTile: React.FC<ContentTileProps> = ({
  link,
  openInNewTab,
  image,
  text,
  altText,
  totalTiles = 5,
}) => {
  return (
    <TileWrapper numberOfTiles={totalTiles}>
      <ConditionalWrapper
        condition={!!link}
        wrapper={(children: JSX.Element) => (
          <StyledLink href={link} target={openInNewTab ? '_blank' : '_self'}>
            {children}
          </StyledLink>
        )}
        falseWrapper={(children: JSX.Element) => (
          <ContentTileContainer>{children}</ContentTileContainer>
        )}
      >
        <>
          {image && <StyledImage src={image} alt={altText} />}
          {text && <TextContainer>{text}</TextContainer>}
        </>
      </ConditionalWrapper>
    </TileWrapper>
  );
};
